/**
 * Fabric Embed Mode Authentication
 *
 * When the IntuigenceAI frontend is embedded in a Fabric workload iframe,
 * authentication is handled via PostMessage rather than Keycloak.
 *
 * The parent Fabric window sends AUTH_TOKEN messages containing an Azure AD
 * token. This module stores and provides that token for API calls.
 */

// PostMessage types (must match FabricPostMessageProtocol.ts in the Fabric workload)
export enum FabricMessageType {
  APP_READY = 'APP_READY',
  AUTH_TOKEN = 'AUTH_TOKEN',
  AUTH_TOKEN_EXPIRED = 'AUTH_TOKEN_EXPIRED',
  AUTH_TOKEN_REFRESH = 'AUTH_TOKEN_REFRESH',
  FABRIC_CONTEXT = 'FABRIC_CONTEXT',
  FABRIC_THEME = 'FABRIC_THEME',
  APP_ERROR = 'APP_ERROR',
}

interface FabricMessage<T = unknown> {
  type: FabricMessageType
  payload: T
}

interface AuthTokenPayload {
  token: string
  workspaceId?: string
}

export const ALLOWED_ORIGINS: string[] = [
  'https://app.fabric.microsoft.com',
  'https://app.powerbi.com',
  // Deployed Fabric workload hosts (one per env). The bundle is shared across envs,
  // so ALL hosts must be listed or embed-mode postMessage auth silently fails there.
  // Kept in sync with fabric/env-config.json by the fabric-ci drift cross-check.
  'https://fabric.dev.aws.intuigence.ai',
  'https://fabric.staging.aws.intuigence.ai',
  // GCP dev/staging hosts: kept only until the GCP us-central1 dev/staging decommission
  // removes them, so the live workloads keep authenticating through the cutover.
  'https://fabric.dev.gcp.intuigence.ai',
  'https://fabric.staging.gcp.intuigence.ai',
  'https://fabric.demo.intuigence.ai',
  // The fork's still-live PROD workload (IntuigenceAI.IndustrialWorkers) is hosted here
  // (Azure Static Web App) and points at the dev backend. It must stay allow-listed until
  // prod is migrated off the fork — removing it (done prematurely in the monorepo migration)
  // breaks embed-mode postMessage auth for the live prod workload. Remove on fork decommission.
  'https://fabric-dev.intuigence.ai',
]
// Allow localhost origins in development
if (process.env.NODE_ENV === 'development') {
  ALLOWED_ORIGINS.push('http://localhost:60006', 'http://127.0.0.1:60006')
}

let embedToken: string | null = null
let embedWorkspaceId: string | null = null
let isEmbedMode = false
let messageListenerAttached = false
let tokenResolver: ((token: string) => void) | null = null
let tokenPromise: Promise<string> | null = null
let parentOrigin: string | null = null

/** Check if the app is running in Fabric embed mode. */
export function isFabricEmbedMode(): boolean {
  return isEmbedMode
}

/** Get the current embed token (or null if not in embed mode or no token yet). */
export function getEmbedToken(): string | null {
  return embedToken
}

/**
 * Seconds until the stored embed token's `exp` (negative if already expired).
 * Null when there is no token or its payload cannot be decoded. Diagnostics
 * only: never returns or logs the token itself.
 */
export function getEmbedTokenTtlSeconds(): number | null {
  if (!embedToken) return null
  try {
    const payload = embedToken.split('.')[1]
    if (!payload) return null
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '='))
    const exp = (JSON.parse(json) as { exp?: unknown }).exp
    if (typeof exp !== 'number' || !Number.isFinite(exp)) return null
    return Math.round(exp - Date.now() / 1000)
  } catch {
    return null
  }
}

/** Get the Fabric workspace ID passed via PostMessage. */
export function getEmbedWorkspaceId(): string | null {
  return embedWorkspaceId
}

/** Get the verified parent origin (set after first AUTH_TOKEN from a valid origin). */
export function getParentOrigin(): string | null {
  return parentOrigin
}

/**
 * Wait for the embed auth token to arrive via PostMessage.
 * Returns immediately if the token is already available.
 * Rejects after timeoutMs if no token arrives.
 */
export function waitForEmbedToken(timeoutMs = 10000): Promise<string> {
  if (embedToken) return Promise.resolve(embedToken)
  if (!tokenPromise) {
    tokenPromise = new Promise<string>((resolve, reject) => {
      tokenResolver = resolve
      setTimeout(() => {
        if (!embedToken) {
          tokenResolver = null
          tokenPromise = null
          reject(new Error('Embed auth token not received within timeout'))
        }
      }, timeoutMs)
    })
  }
  return tokenPromise
}

// Waiters for the next token that differs from the stale one they were handed.
const refreshWaiters = new Set<(token: string) => void>()

/**
 * Ask the parent for a fresh token after a 401 and resolve with the next
 * AUTH_TOKEN / AUTH_TOKEN_REFRESH whose token differs from `staleToken`.
 *
 * A re-sent identical token is ignored: the parent used to answer with the
 * very token that had just expired (27 Oct demo), and retrying with it would
 * only 401 again. Rejects after `timeoutMs`, or when the optional `signal`
 * aborts (the caller's own cancellation / overall request deadline).
 */
export function refreshEmbedToken(
  staleToken: string,
  timeoutMs = 10_000,
  signal?: AbortSignal
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const timer = setTimeout(() => {
      cleanup()
      reject(new Error('Embed auth token refresh not received within timeout'))
    }, timeoutMs)
    const cleanup = () => {
      refreshWaiters.delete(onToken)
      clearTimeout(timer)
      signal?.removeEventListener('abort', onAbort)
    }
    const onToken = (token: string) => {
      if (token === staleToken) return
      cleanup()
      resolve(token)
    }
    const onAbort = () => {
      cleanup()
      reject(signal?.reason ?? new Error('Embed token refresh aborted'))
    }
    if (signal?.aborted) {
      onAbort()
      return
    }
    refreshWaiters.add(onToken)
    signal?.addEventListener('abort', onAbort, { once: true })
    signalTokenExpired()
  })
}

/** Set embed mode (called on app initialization when ?embed=fabric is detected). */
export function enableFabricEmbedMode(): void {
  isEmbedMode = true
  attachMessageListener()
  signalAppReady()
}

/** Post a message to the parent window using the verified parent origin. */
function postToParent(message: FabricMessage): void {
  if (!window.parent || window.parent === window) return
  // Use the verified parent origin if available, otherwise try production origin
  const targetOrigin = parentOrigin || ALLOWED_ORIGINS[0]!
  window.parent.postMessage(message, targetOrigin)
}

/** Send APP_READY signal to parent window. */
function signalAppReady(): void {
  // APP_READY is sent before we know the parent origin — use '*' for this
  // initial handshake only. All subsequent messages use the verified origin.
  if (window.parent && window.parent !== window) {
    const message: FabricMessage = {
      type: FabricMessageType.APP_READY,
      payload: {},
    }
    window.parent.postMessage(message, '*')
  }
}

/** Send AUTH_TOKEN_EXPIRED signal to parent window. */
export function signalTokenExpired(): void {
  if (!isEmbedMode) return
  postToParent({
    type: FabricMessageType.AUTH_TOKEN_EXPIRED,
    payload: {},
  })
}

/** Attach PostMessage listener for auth tokens from parent. */
function attachMessageListener(): void {
  if (messageListenerAttached) return
  messageListenerAttached = true

  window.addEventListener('message', (event: MessageEvent) => {
    // Validate origin — only accept messages from allowed Fabric origins
    if (!ALLOWED_ORIGINS.includes(event.origin)) {
      return
    }

    const message = event.data as FabricMessage
    if (!message?.type) return

    switch (message.type) {
      case FabricMessageType.AUTH_TOKEN:
      case FabricMessageType.AUTH_TOKEN_REFRESH: {
        const payload = message.payload as AuthTokenPayload
        if (payload?.token) {
          // Store the verified parent origin for future outbound messages
          parentOrigin = event.origin
          embedToken = payload.token
          if (payload.workspaceId) {
            embedWorkspaceId = payload.workspaceId
          }
          for (const waiter of [...refreshWaiters]) waiter(payload.token)
          // Resolve any callers waiting for the initial token
          if (tokenResolver) {
            tokenResolver(payload.token)
            tokenResolver = null
            tokenPromise = null
          }
        }
        break
      }
    }
  })
}
